<template>
  <v-card flat class="product-editor">
    <!-- Header -->
    <div class="product-header px-4 py-3">
      <div class="d-flex align-center">
        <v-btn icon small class="mr-2" @click="close" :disabled="saving" title="Cerrar">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <div class="header-icon mr-3">
          <v-icon color="white" size="20">mdi-tag-text</v-icon>
        </div>
        <div class="flex-grow-1">
          <div class="text-subtitle-1 font-weight-medium">Producto de {{ thirdKindLabel.toLowerCase() }}</div>
          <div class="text-caption grey--text" v-if="localData.nombre_producto">{{ localData.nombre_producto }}</div>
        </div>
        <v-btn color="primary" small depressed @click="handleSave" :loading="saving" :disabled="!isValid || saving" class="mr-2">
          <v-icon left small>mdi-content-save</v-icon>
          Guardar
        </v-btn>
        <v-btn icon small @click="close" :disabled="saving" title="Cerrar"><v-icon>mdi-close</v-icon></v-btn>
      </div>
    </div>

    <v-divider></v-divider>

    <v-card-text class="pa-4 pt-3">
      <v-alert v-if="localData.isPriceMissing" dense text type="warning" class="mb-4">
        Este producto se importó sin precio. Introduzca un precio para completar su ficha.
      </v-alert>
      <div class="section-label mb-2">
        <v-icon small class="mr-1">mdi-domain</v-icon>
        {{ thirdKindLabel }}
      </div>
      <v-row dense>
        <v-col v-if="canChooseProductKind" cols="12" sm="4">
          <v-select
            v-model="localData.kind"
            :items="productKindList"
            label="Tipo de producto"
            outlined
            dense
            hide-details="auto"
            :disabled="saving"
            @change="changeProductKind"
          ></v-select>
        </v-col>
        <v-col cols="12" :sm="canChooseProductKind ? 8 : 12">
          <SupplierAutocomplete
            v-if="localData.kind !== 'out_invoice'"
            :company-id="companyId"
            :supplier-tax-id="localData.cif_proveedor"
            :supplier-name="localData.nombre_proveedor"
            :disabled="saving"
            @select="selectSupplier"
          />
          <CustomerAutocomplete
            v-else
            :company-id="companyId"
            :customer-tax-id="localData.cif_proveedor"
            :customer-name="localData.nombre_proveedor"
            :disabled="saving"
            @select="selectCustomer"
          />
        </v-col>
      </v-row>

      <!-- Producto Section -->
      <div class="section-label mb-2 mt-4">
        <v-icon small class="mr-1">mdi-package-variant-closed</v-icon>
        Producto
      </div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field :value="localData.nombre_producto" @input="val => { localData.nombre_producto = val; update(); }"
            label="Descripción del producto" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field :value="formatPrice(localData.precio_unitario)"
            label="Precio actual" readonly outlined dense hide-details="auto"
            class="field-input price-field readonly-field"></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field :value="localData.tax_percent" @input="val => { localData.tax_percent = parseFloat(val) || 0; update(); }"
            :label="'% ' + taxLabel" type="number" step="0.01" min="0" max="100" outlined dense hide-details="auto"
            suffix="%" class="field-input price-field"></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field :value="localData.unidad_medida" @input="val => { localData.unidad_medida = val; update(); }"
            label="Unidad" placeholder="kg, caja, unidad..." outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
      </v-row>

      <v-row dense class="mt-2">
        <v-col cols="12" sm="3">
          <v-text-field :value="localData.category" @input="val => { localData.category = val; update(); }"
            label="Categoría" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-text-field :value="localData.ean" @input="val => { localData.ean = val; update(); }"
            label="EAN" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="4" sm="2">
          <v-text-field :value="localData.units_per_case" @input="val => { localData.units_per_case = parseInt(val) || 0; update(); }"
            label="Ud./caja" type="number" min="0" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="4" sm="2">
          <v-text-field :value="localData.package_weight" @input="val => { localData.package_weight = parseFloat(val) || 0; update(); }"
            label="P. Esc" type="number" step="0.01" min="0" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="4" sm="2">
          <v-text-field :value="localData.variation" @input="val => { localData.variation = val; update(); }"
            label="Variación" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
      </v-row>

      <!-- Códigos Section -->
      <div class="section-label mb-2 mt-4">
        <v-icon small class="mr-1">mdi-barcode</v-icon>
        Identificadores
      </div>
      <v-row dense>
        <v-col cols="12" sm="6">
          <v-text-field :value="localData.codigo_interno" @input="val => { localData.codigo_interno = val; update(); }"
            label="Código interno" placeholder="Tu referencia" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
        <v-col cols="12" sm="6">
          <v-text-field :value="localData.codigo_externo" @input="val => { localData.codigo_externo = val; update(); }"
            label="Código externo" :placeholder="externalCodePlaceholder" outlined dense hide-details="auto" class="field-input"></v-text-field>
        </v-col>
      </v-row>

      <v-divider class="mt-5"></v-divider>

      <div class="section-label mb-2 mt-4">
        <v-icon small class="mr-1">mdi-chart-line-variant</v-icon>
        Precios por fecha
      </div>
      <div class="text-caption grey--text mb-2">
        Cada precio rige hasta que empieza el siguiente; una factura se contrasta con el de su fecha.
      </div>
      <apexchart
        v-if="priceChartSeries[0].data.length > 1"
        type="line"
        height="180"
        :options="priceChartOptions"
        :series="priceChartSeries"
      ></apexchart>

      <div class="price-series">
        <div v-if="!sortedPriceList.length" class="text-body-2 grey--text pa-4 text-center">
          Este producto todavía no tiene precios fechados.
        </div>
        <div
          v-for="(entry, index) in sortedPriceList"
          :key="'price-' + index"
          class="price-row d-flex align-center px-3 py-2"
          :class="{ 'price-row--current': index === currentPriceIndex,
                    'price-row--future': entry.date > todayIsoDate }"
        >
          <v-icon small class="mr-3 grey--text">mdi-calendar-start</v-icon>
          <input :value="entry.date" @change="changePriceDate(entry, $event.target.value)"
            type="date" class="price-input price-input--date" />
          <span class="price-period text-caption grey--text mx-3">{{ periodLabel(index) }}</span>
          <v-spacer></v-spacer>
          <v-chip v-if="index === currentPriceIndex" x-small color="primary" outlined
            class="mr-3">Vigente</v-chip>
          <v-chip v-else-if="entry.date > todayIsoDate" x-small color="grey" outlined
            class="mr-3">Futura</v-chip>
          <input :value="entry.price" @change="changePriceValue(entry, $event.target.value)"
            type="number" step="0.01" min="0" class="price-input price-input--money" />
          <span class="grey--text text--darken-1 ml-1 mr-2">€</span>
          <v-btn icon x-small @click="removePriceEntry(entry)" title="Quitar este precio">
            <v-icon small class="grey--text">mdi-close</v-icon>
          </v-btn>
        </div>

        <div class="price-row price-row--new d-flex align-center px-3 py-2">
          <v-icon small class="mr-3 grey--text">mdi-plus</v-icon>
          <input v-model="newPriceDate" type="date" class="price-input price-input--date" />
          <span class="price-period text-caption grey--text mx-3">nuevo tramo</span>
          <v-spacer></v-spacer>
          <input v-model="newPriceValue" type="number" step="0.01" min="0" placeholder="0,00"
            class="price-input price-input--money" @keyup.enter="addPriceEntry" />
          <span class="grey--text text--darken-1 ml-1 mr-2">€</span>
          <v-btn small text color="primary" :disabled="!canAddPrice" @click="addPriceEntry">
            Añadir
          </v-btn>
        </div>
      </div>

      <v-divider v-if="localData.uuid" class="mt-5"></v-divider>

      <div
        v-if="localData.uuid"
        class="section-label history-toggle d-flex align-center mb-2 mt-4"
        @click="toggleHistory"
      >
        <v-icon small class="mr-1">mdi-history</v-icon>
        Historial de cambios
        <span v-if="isHistoryVisible && historyTotalCount" class="text-caption grey--text ml-2">
          {{ historyTotalCount }}
        </span>
        <v-spacer></v-spacer>
        <span v-if="!isHistoryVisible" class="text-caption grey--text mr-2">ver</span>
        <v-icon small class="grey--text">
          {{ isHistoryVisible ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
        </v-icon>
      </div>
      <div v-if="localData.uuid && isHistoryVisible" class="price-history">
        <div v-if="historyLoading" class="text-caption grey--text py-2">
          Cargando histórico…
        </div>
        <div v-else-if="!historyItemList.length" class="text-caption grey--text py-2">
          Este producto todavía no tiene cambios registrados.
        </div>
        <div
          v-for="revision in historyItemList"
          :key="revision.uuid"
          class="price-history-row d-flex align-center justify-space-between py-2"
        >
          <div>
            <div v-for="(change, field) in revision.changeMap" :key="revision.uuid + '-' + field"
              class="d-flex align-baseline flex-wrap text-body-2 mb-1">
              <span class="history-field grey--text text--darken-2 mr-2">{{ fieldLabel(field) }}</span>
              <template v-if="isOpaqueChange(field)">
                <span class="font-weight-bold">Tarifas modificadas</span>
              </template>
              <template v-else>
                <span v-if="hasPreviousValue(change)" class="grey--text history-old-value">
                  {{ formatHistoryValue(field, change.before) }}
                </span>
                <v-icon v-if="hasPreviousValue(change)" x-small class="mx-2 grey--text">mdi-arrow-right</v-icon>
                <span class="font-weight-bold">{{ formatHistoryValue(field, change.after) }}</span>
              </template>
            </div>
            <div class="text-caption grey--text">
              {{ sourceLabel(revision.source) }}
            </div>
          </div>
          <span class="text-caption grey--text">{{ formatRevisionDate(revision.changedAt) }}</span>
        </div>
        <div v-if="historyLoadedCount < historyTotalCount" class="text-center pt-3">
          <v-btn small text color="primary" :loading="historyLoading" @click="loadMoreHistory">
            Cargar más
          </v-btn>
        </div>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
import { ApiProductRepository } from './ApiProductRepository';
import SupplierAutocomplete from './SupplierAutocomplete.vue';
import CustomerAutocomplete from './CustomerAutocomplete.vue';
import VueApexCharts from 'vue-apexcharts';
import { mapGetters } from 'vuex';
import { getDefaultInvoiceableTaxRate, getInvoiceableTaxLabel } from '@/modules/facturae/domain/InvoiceableTax';

// Changes shown as a plain summary instead of a before/after pair.
const OPAQUE_HISTORY_FIELD_LIST = ['price_list'];

const EMPTY_PRODUCT = (taxPercent = 21, kind = 'in_invoice') => ({
  uuid: '',
  kind,
  cif_proveedor: '',
  nombre_proveedor: '',
  nombre_producto: '',
  codigo_interno: '',
  codigo_externo: '',
  unidad_medida: '',
  precio_unitario: 0,
  tax_percent: taxPercent,
  ean: '',
  category: '',
  units_per_case: 0,
  package_weight: 0,
  variation: '',
  isPriceMissing: false,
  priceList: [],
});

export default {
  name: 'ProductEditorComponent',
  components: { SupplierAutocomplete, CustomerAutocomplete, apexchart: VueApexCharts },
  props: {
    companydb_id: { type: [Number, String], required: true },
    product_id: { type: [String], required: false, default: null },
    initialKind: { type: String, required: false, default: 'in_invoice' },
    lockKind: { type: Boolean, required: false, default: false },
  },
  data() {
    return {
      repo: new ApiProductRepository(),
      localData: EMPTY_PRODUCT(),
      loaded: false,
      saving: false,
      historyLoading: false,
      historyItemList: [],
      historyTotalCount: 0,
      historyLoadedCount: 0,
      isHistoryVisible: false,
      newPriceDate: '',
      newPriceValue: '',
      productKindList: [
        { text: 'Proveedor (compra)', value: 'in_invoice' },
        { text: 'Cliente (venta)', value: 'out_invoice' },
      ],
    };
  },
  computed: {
    sortedPriceList() {
      return [...(this.localData.priceList || [])].sort((left, right) => (
        left.date < right.date ? -1 : left.date > right.date ? 1 : 0
      ));
    },
    todayIsoDate() {
      const today = new Date();
      const month = String(today.getMonth() + 1).padStart(2, '0');
      const day = String(today.getDate()).padStart(2, '0');
      return today.getFullYear() + '-' + month + '-' + day;
    },
    // What rules today is the last entry already started, not the last one on
    // the list: a future tariff is loaded but not in force yet.
    currentPriceIndex() {
      const startedList = this.sortedPriceList.filter(
        entry => entry.date <= this.todayIsoDate,
      );
      if (!startedList.length) return this.sortedPriceList.length ? 0 : -1;
      return this.sortedPriceList.indexOf(startedList[startedList.length - 1]);
    },
    canAddPrice() {
      return Boolean(this.newPriceDate) && parseFloat(this.newPriceValue) >= 0
        && this.newPriceValue !== '';
    },
    priceChartSeries() {
      return [{
        name: 'Precio',
        data: this.sortedPriceList.map(entry => ({
          x: entry.date,
          y: entry.price,
        })),
      }];
    },
    priceChartOptions() {
      return {
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        // The price holds flat until the next entry, so the line must step.
        stroke: { curve: 'stepline', width: 2 },
        markers: { size: 4 },
        dataLabels: { enabled: false },
        xaxis: { type: 'datetime' },
        yaxis: {
          labels: {
            formatter: value => this.formatPrice(value),
          },
        },
        tooltip: { x: { format: 'dd/MM/yyyy' } },
        colors: ['#1976d2'],
        grid: { borderColor: '#eee' },
      };
    },
    ...mapGetters({ currentCompany: 'getCurrentCompany' }),
    access() { return this.$store.getters['permissions/albaranAccess']; },
    customerDocumentsEnabled() { return Boolean(this.access.customer_documents_enabled); },
    canChooseProductKind() { return this.customerDocumentsEnabled && !this.lockKind; },
    companyId() { return parseInt(this.companydb_id); },
    taxType() { return this.currentCompany && this.currentCompany.tax_type ? this.currentCompany.tax_type : 'iva'; },
    taxLabel() { return getInvoiceableTaxLabel(this.taxType); },
    defaultTaxPercent() { return getDefaultInvoiceableTaxRate(this.taxType) * 100; },
    thirdKindLabel() { return this.localData.kind === 'out_invoice' ? 'Cliente' : 'Proveedor'; },
    externalCodePlaceholder() {
      return this.localData.kind === 'out_invoice' ? 'Referencia del cliente' : 'Referencia del proveedor';
    },
    isValid() {
      return this.localData.cif_proveedor &&
             this.localData.nombre_producto &&
             this.localData.precio_unitario > 0 &&
             this.localData.unidad_medida;
    },
  },
  async mounted() {
    await this.loadProduct();
  },
  methods: {
    selectSupplier(supplier) {
      this.selectThird(supplier);
    },
    selectCustomer(customer) {
      this.selectThird(customer);
    },
    selectThird(third) {
      this.localData.cif_proveedor = third ? third.cif : '';
      this.localData.nombre_proveedor = third ? third.name : '';
    },
    changeProductKind() {
      this.selectThird(null);
    },
    // localData is component-owned; edits are reactive in place.
    update() {},
    // The series is the source of truth: the current price is its last entry,
    // so editing the past never rewrites what the product is worth today.
    applyPriceList(priceList) {
      const sortedList = [...priceList].sort((left, right) => (
        left.date < right.date ? -1 : left.date > right.date ? 1 : 0
      ));
      this.localData.priceList = sortedList;
      if (sortedList.length) {
        this.localData.precio_unitario = sortedList[sortedList.length - 1].price;
        this.localData.isPriceMissing = false;
      }
    },
    addPriceEntry() {
      if (!this.canAddPrice) return;
      const price = parseFloat(this.newPriceValue) || 0;
      // One date, one price: re-entering a date corrects it.
      const keptList = (this.localData.priceList || []).filter(
        entry => entry.date !== this.newPriceDate,
      );
      this.applyPriceList([...keptList, { date: this.newPriceDate, price }]);
      this.newPriceDate = '';
      this.newPriceValue = '';
    },
    changePriceDate(entry, newDate) {
      if (!newDate) return;
      const keptList = (this.localData.priceList || []).filter(
        item => item !== entry && item.date !== newDate,
      );
      this.applyPriceList([...keptList, { date: newDate, price: entry.price }]);
    },
    changePriceValue(entry, newPrice) {
      const price = parseFloat(newPrice) || 0;
      const keptList = (this.localData.priceList || []).filter(item => item !== entry);
      this.applyPriceList([...keptList, { date: entry.date, price }]);
    },
    removePriceEntry(entry) {
      this.applyPriceList(
        (this.localData.priceList || []).filter(item => item !== entry),
      );
    },
    close() {
      // Emitido para que el contenedor cierre el popup; en el caso ruta-standalone
      // (ProductCatalogEditor.vue) escucha y navega hacia atrás.
      this.$emit('close');
    },
    async loadProduct() {
      this.loaded = false;
      const id = this.product_id || this.localData.uuid;
      if (id) {
        const data = await this.repo.getById(id, this.companyId);
        this.localData = { ...EMPTY_PRODUCT(this.defaultTaxPercent, this.initialKind), ...(data || {}) };
      } else {
        this.localData = EMPTY_PRODUCT(this.defaultTaxPercent, this.initialKind);
        this.historyItemList = [];
        this.historyTotalCount = 0;
      }
      this.loaded = true;
    },
    async handleSave() {
      if (this.saving || !this.isValid) return;
      this.saving = true;
      try {
        const uuid = await this.repo.save(this.localData, this.companyId);
        if (this.isHistoryVisible) await this.loadHistory();
        // Emitir SIEMPRE tras guardar (tanto en alta como en edición): el
        // contenedor refresca el listado. En alta, el repo injerta el uuid en
        // localData, así que lo propagamos.
        this.$emit('saved', uuid || this.product_id || this.localData.uuid);
      } catch (e) { console.error('Save failed:', e); }
      finally { this.saving = false; }
    },
    formatPrice(price) {
      return new Intl.NumberFormat('es-ES', {
        style: 'currency',
        currency: 'EUR',
      }).format(price || 0);
    },
    // Kept collapsed so opening a product does not pay for the history it
    // rarely needs; the first expand loads it.
    async toggleHistory() {
      this.isHistoryVisible = !this.isHistoryVisible;
      if (this.isHistoryVisible && !this.historyItemList.length) {
        await this.loadHistory();
      }
    },
    async loadHistory() {
      if (!this.localData.uuid) return;
      this.historyLoading = true;
      try {
        const result = await this.repo.history(this.localData.uuid, this.companyId, 20, 0);
        this.historyItemList = result.items;
        this.historyTotalCount = result.total_count;
        this.historyLoadedCount = result.loadedCount;
      } catch (e) {
        this.historyItemList = [];
        this.historyTotalCount = 0;
        this.historyLoadedCount = 0;
      } finally {
        this.historyLoading = false;
      }
    },
    async loadMoreHistory() {
      if (!this.localData.uuid || this.historyLoading) return;
      this.historyLoading = true;
      try {
        const result = await this.repo.history(
          this.localData.uuid,
          this.companyId,
          20,
          this.historyLoadedCount,
        );
        this.historyItemList = [...this.historyItemList, ...result.items];
        this.historyTotalCount = result.total_count;
        this.historyLoadedCount = this.historyLoadedCount + result.loadedCount;
      } finally {
        this.historyLoading = false;
      }
    },
    sourceLabel(source) {
      return ({
        manual: 'Cambio manual',
        excel: 'Importación Excel',
        pdf: 'Importación PDF',
        bulk: 'Cambio masivo',
        repair: 'Corrección',
        supplier_record: 'Ficha del proveedor',
      })[source] || 'Actualización';
    },
    // Each price rules until the next one starts; the last one has no end.
    periodLabel(index) {
      const nextEntry = this.sortedPriceList[index + 1];
      if (!nextEntry) return 'en adelante';
      const endDate = new Date(nextEntry.date + 'T00:00:00');
      endDate.setDate(endDate.getDate() - 1);
      return 'hasta ' + new Intl.DateTimeFormat('es-ES').format(endDate);
    },
    // Arrays read as noise in a before/after line, so they are summarised.
    isOpaqueChange(field) {
      return OPAQUE_HISTORY_FIELD_LIST.indexOf(field) !== -1;
    },
    fieldLabel(field) {
      return ({
        nombre_producto: 'Producto',
        precio_unitario: 'Precio',
        price_list: 'Tarifas',
        tax_percent: 'Impuesto',
        category: 'Categoría',
        unidad_medida: 'Unidad',
        codigo_interno: 'Código interno',
        codigo_externo: 'Código externo',
        ean: 'EAN',
        cif_proveedor: 'CIF del tercero',
        nombre_proveedor: 'Tercero',
        units_per_case: 'Unidades por caja',
        package_weight: 'Peso del envase',
        variation: 'Variación',
        is_price_missing: 'Precio pendiente',
      })[field] || field;
    },
    hasPreviousValue(change) {
      const previousValue = change.before;
      return previousValue !== null && previousValue !== undefined && previousValue !== '';
    },
    formatHistoryValue(field, value) {
      if (value === null || value === undefined || value === '') return 'Sin valor';
      if (field === 'precio_unitario') return this.formatPrice(value);
      if (field === 'tax_percent') return Number(value).toFixed(2) + ' %';
      if (typeof value === 'boolean') return value ? 'Sí' : 'No';
      return String(value);
    },
    formatRevisionDate(value) {
      if (!value) return '';
      return new Intl.DateTimeFormat('es-ES', {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(value));
    },
  },
};
</script>

<style scoped>
.history-toggle {
  cursor: pointer;
  user-select: none;
}
.history-toggle:hover {
  color: #1976d2;
}
.history-field {
  min-width: 132px;
}
.product-editor .readonly-field >>> .v-input__slot {
  background: #fafafa;
}
.product-editor .readonly-field >>> input {
  color: rgba(0, 0, 0, 0.7);
}
.price-series {
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 6px;
  overflow: hidden;
}
.price-row + .price-row {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}
.price-row--current {
  background: rgba(25, 118, 210, 0.04);
}
.price-row--future {
  background: repeating-linear-gradient(
    45deg, transparent, transparent 6px,
    rgba(0, 0, 0, 0.015) 6px, rgba(0, 0, 0, 0.015) 12px
  );
}
.price-row--new {
  background: #fafafa;
}
.price-period {
  min-width: 110px;
}
.price-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.875rem;
  color: rgba(0, 0, 0, 0.87);
}
.price-input:focus {
  border-bottom: 1px solid #1976d2;
}
.price-input--date {
  width: 130px;
}
.price-input--money {
  width: 80px;
  text-align: right;
  font-weight: 600;
}
.price-row--current .price-input--money {
  color: #1976d2;
}
.history-old-value {
  text-decoration: line-through;
}
.product-editor .product-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
}
.product-editor .header-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
}
.product-editor .section-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #78909c;
  display: flex;
  align-items: center;
}
.product-editor .field-input {
  background: #fafafa;
  border-radius: 8px;
}
.product-editor .price-field input {
  text-align: right;
  font-weight: 600;
}
.product-editor .summary-card {
  background: linear-gradient(135deg, #e3f2fd 0%, #f3e5f5 100%);
  border-radius: 8px;
  border-left: 3px solid #1976d2;
}
.product-editor .price-display {
  font-size: 18px;
  font-weight: 700;
  color: #1976d2;
}
.product-editor .price-unit {
  font-size: 13px;
  font-weight: 500;
  color: #78909c;
}
.product-editor .price-history {
  border-top: 1px solid #eceff1;
}
.product-editor .price-history-row {
  border-bottom: 1px solid #eceff1;
}
</style>
